import React, { HTMLAttributes, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import cx from "classnames";
import { Hooks } from "@xt/xtbase";
const { useTranslation } = Hooks;
import store from "store";
import { post_symbolStar, delete_symbolStarId } from "api/v4/account";

import { Tooltip, TooltipProps } from "antd";
import AppDivToLogin from "@/components/App/div/toLogin";

import styles from "./index.module.scss";

const STAR_OUTLINE_PATH =
  "m10.272 15.984-6.348 3.553 1.418-7.135L0 7.462l7.224-.856L10.272 0l3.047 6.606 7.224.857-5.341 4.939 1.417 7.135zm0-2.063 3.822 2.14-.854-4.296 3.216-2.975-4.35-.515-1.834-3.977-1.835 3.977-4.35.515 3.217 2.975-.855 4.296z";

/** 外轮廓闭合路径，收藏成功时绿色实心填充 */
const STAR_FILLED_PATH =
  "M10.272 15.984L3.924 19.537L5.342 12.402L0 7.462L7.224 6.606L10.272 0L13.319 6.606L20.543 7.463L15.202 12.402L16.619 19.537L10.272 15.984Z";

interface Props extends HTMLAttributes<HTMLButtonElement> {
  symbol: string;
  placement?: TooltipProps["placement"];
  isStarEmpty?: boolean;
  /** 未收藏时图标颜色，如 "#fff"；收藏态固定为主题绿 */
  iconColor?: string;
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none" aria-hidden="true">
      <path className={styles.starPath} d={filled ? STAR_FILLED_PATH : STAR_OUTLINE_PATH} />
    </svg>
  );
}

const Main: React.FC<Props> = ({ symbol, className, placement = "topLeft", iconColor }) => {
  const t = useTranslation();
  const { isLogin, symbolStar } = store.user;

  const atvDoc: WithUndefined<string> = useMemo(() => {
    if (!isLogin) return;
    return symbolStar.find((obj) => obj === symbol);
  }, [isLogin, symbol, symbolStar]);
  const tip = useMemo(() => {
    return !atvDoc ? t("market.addToFavorites") : t("market.favRemove");
  }, [atvDoc, t]);

  const [loading, setLoading] = useState(false);
  const handleClick = (e) => {
    e && e.stopPropagation();
    if (!isLogin || loading) return;
    setLoading(true);

    if (atvDoc) {
      delete_symbolStarId(atvDoc)
        .then(() => {
          store.user.getSymbolStar();
        })
        .catch(() => {
          //empty
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      post_symbolStar({
        params: { symbol },
      })
        .then(() => {
          store.user.getSymbolStar();
        })
        .catch(() => {
          //empty
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const btnStyle = iconColor && !atvDoc ? { color: iconColor } : undefined;

  const button = (
    <button
      className={cx("btnTxt", styles.main, className, { [styles.atv]: !!atvDoc })}
      style={btnStyle}
      onClick={handleClick}
      type="button"
      aria-label={tip}
    >
      <StarIcon filled={!!atvDoc} />
    </button>
  );

  if (isLogin) {
    return button;
  }

  return (
    <Tooltip
      trigger="click"
      placement={placement}
      getPopupContainer={(triggerNode: HTMLElement) => triggerNode}
      title={<AppDivToLogin />}
      overlayStyle={{ whiteSpace: "nowrap" }}
    >
      {button}
    </Tooltip>
  );
};

export default observer(Main);
